import { Button, Checkbox, Flex, Form, Radio, Tooltip } from "antd"
import { WarningIcon } from "ui/Icon/DSIcon/WarningIcon"
import { useTranslation } from "react-i18next"
import { spacings } from "themes/variables"
import { Text, Title } from "ui/Typography"
import WeekDays from "./WeekDays"
import DateRange from "./DateRange"
import SingleDay from "./SingleDay"
import { TextArea } from "ui/Input"
import { TIME_OFF_BE_KEYS } from "pagesV2/pro/Calendar/constants"
import PropTypes from "prop-types"
import styles from "./index.module.css"
import { DeleteTimeOff } from "../.."
import { useTimeOffDeletion } from "../hooks/useTimeOffDeletion"

const getSwitcherComponent = (form, editingTimeOffItem) => {
  return {
    weekdays: <WeekDays form={form} editingTimeOffItem={editingTimeOffItem} />,
    range: <DateRange />,
    date: <SingleDay form={form} />
  }
}

const TimeOffTypeSwitcher = ({ form, editingTimeOffItem, setEditingTimeOffItem, dates }) => {
  const { t } = useTranslation()
  const timeOffType = Form.useWatch("timeOffType", form) || TIME_OFF_BE_KEYS.weekDays
  const isDisabled = !!editingTimeOffItem
  const themeColor = "var(--color-primary)"

  const { isDeleteModalOpen, handleDeleteTimeOff, handleCancelDeleteTimeOff, handleYesDeleteTimeOff } = useTimeOffDeletion({
    setEditingTimeOffItem,
    form,
    timeOffType,
    dates
  })

  return (
    <>
      <Flex align="center" gap={spacings.S}>
        <Title level={4}>{t("calendar.schedule_time_off.set_time_off_type")}</Title>
        <Tooltip title={t("calendar.schedule_time_off.timeoff_type_warning")}>
          <WarningIcon fill={themeColor} />
        </Tooltip>
      </Flex>
      <Form.Item name="timeOffType" initialValue={TIME_OFF_BE_KEYS.weekDays} className="m-0">
        <Radio.Group value={timeOffType} onChange={(e) => !isDisabled && form.setFieldsValue({ timeOffType: e.target.value })}>
          <Radio className="my-s" value={TIME_OFF_BE_KEYS.weekDays} disabled={isDisabled}>
            {t("calendar.schedule_time_off.weekdays")}
          </Radio>
          <Radio value={TIME_OFF_BE_KEYS.dateRange} disabled={isDisabled}>
            {t("calendar.schedule_time_off.date_range")}
          </Radio>
          <Radio value={TIME_OFF_BE_KEYS.singleDay} disabled={isDisabled}>
            {t("calendar.schedule_time_off.single_day")}
          </Radio>
        </Radio.Group>
      </Form.Item>
      {getSwitcherComponent(form, editingTimeOffItem)[timeOffType]}
      <Flex gap={spacings.XS} align="center">
        <Form.Item name="recurringYearly" valuePropName="checked" className="m-0">
          <Checkbox />
        </Form.Item>
        <Text>{t("calendar.schedule_time_off.recurring_yearly")}</Text>
      </Flex>
      <Flex vertical gap={spacings.XXS}>
        <Text fw={500}>{t("calendar.schedule_time_off.notes")}</Text>
        <Form.Item name="notes" className="m-0">
          <TextArea rows={4} />
        </Form.Item>
      </Flex>
      {editingTimeOffItem && (
        <Button
          className={`justify-start w-fit ${styles.deleteButton}`}
          type="link"
          onClick={() => handleDeleteTimeOff(editingTimeOffItem?.id)}
        >
          {t("calendar.schedule_time_off.delete_time_off")}
        </Button>
      )}
      {isDeleteModalOpen && <DeleteTimeOff open={isDeleteModalOpen} onCancel={handleCancelDeleteTimeOff} onYes={handleYesDeleteTimeOff} />}
    </>
  )
}

TimeOffTypeSwitcher.propTypes = {
  form: PropTypes.object,
  editingTimeOffItem: PropTypes.object,
  setEditingTimeOffItem: PropTypes.func,
  dates: PropTypes.object
}

export default TimeOffTypeSwitcher
